import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";
import { getUserInfo } from "./userActions";
import { getHomeCalendar } from "./home";
import { friendlyReadError } from "../../services/readRequest";
import { toResponseErrorText } from "../../services/errorText";
import {
  createRequestDedupe,
  SCREEN_ENTRY_THROTTLE_MS,
} from "./requestDedupe";
import { updateUserProperty } from "../reducer/userSlice";
import {
  clearCalendarCache,
  setCalendarCache,
} from "../../services/calendarCache";
import { getAuthToken } from "../../services/authStorage";

const safeJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

const patchJson = async (url, payload) => {
  const res = await fetch(url, {
    method: "PATCH",
    headers: headersDefault(),
    body: JSON.stringify(payload),
  });
  const data = await safeJson(res);
  if (!res.ok) {
    const err = new Error(data?.message || "Errore durante il salvataggio.");
    err.data = data;
    throw err;
  }
  return data;
};

const buildQuery = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    sp.set(k, String(v));
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

export const fetchMeasureDiaryByDate = createAsyncThunk(
  "mioPercorso/fetchMeasureDiaryByDate",
  async (date, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/master-club-measure-diary/${date}/fetch`,
        {
          headers: headersDefault(),
        },
      );
      if (!res.ok) {
        const err = await safeJson(res);
        throw new Error(err?.message || "Failed to fetch measures for date");
      }
      const data = await safeJson(res);
      return { date, data };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to fetch measures for date");
    }
  },
);

export const fetchMeasureDiaryInfo = createAsyncThunk(
  "mioPercorso/fetchMeasureDiaryInfo",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${baseUrl}/api/master-club-measure-diary/info`, {
        headers: headersDefault(),
      });
      if (!res.ok) {
        const err = await safeJson(res);
        throw new Error(err?.message || "Failed to fetch journey info");
      }
      const data = await safeJson(res);
      return data;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to fetch journey info");
    }
  },
);

/**
 * Fetch the full measurement history as a flat, date-filtered array
 * (legacy list endpoint: `?start&end` → array of daily entries). Used by the
 * Progressi chart + Confronto table, which slice/filter it client-side.
 */
// Keyed on the window being charted, so asking for a different range always
// reaches the server; only the identical range is reused. The Misure tab reads
// this on entry, and re-entering used to fetch it again immediately.
const measureSeriesDedupe = createRequestDedupe({
  throttleMs: SCREEN_ENTRY_THROTTLE_MS,
  keyOf: (arg) => `${arg?.start ?? ""}..${arg?.end ?? ""}`,
});

export const fetchMeasureDiarySeries = createAsyncThunk(
  "mioPercorso/fetchMeasureDiarySeries",
  async ({ start, end }, { rejectWithValue }) => {
    try {
      return await measureSeriesDedupe.run({ start, end }, async () => {
        const qs = new URLSearchParams({ start, end }).toString();
        const res = await fetch(
          `${baseUrl}/api/master-club-measure-diary/list?${qs}`,
          {
            headers: headersDefault(),
          },
        );
        if (!res.ok) {
          const err = await safeJson(res);
          throw new Error(err?.message || "Failed to fetch measure history");
        }
        const data = await safeJson(res);
        // Endpoint returns a flat array; be defensive about a paginated shape.
        return Array.isArray(data) ? data : (data?.items ?? []);
      });
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to fetch measure history");
    }
  },
  { condition: (arg) => !measureSeriesDedupe.isFresh(arg) },
);

/**
 * Fetch the user's most recently uploaded measure photo (across all dates).
 * Lets the Misure photo box keep showing the latest photo when the selected
 * date has none — so it persists across navigation and day changes. Does NOT
 * touch the Progressi `series` state.
 */
export const fetchLatestMeasurePhoto = createAsyncThunk(
  "mioPercorso/fetchLatestMeasurePhoto",
  async (_, { rejectWithValue }) => {
    try {
      const end = new Date();
      const start = new Date();
      start.setFullYear(start.getFullYear() - 5);
      const fmt = (d) => d.toISOString().slice(0, 10);
      const qs = new URLSearchParams({
        start: fmt(start),
        end: fmt(end),
      }).toString();

      const res = await fetch(
        `${baseUrl}/api/master-club-measure-diary/list?${qs}`,
        { headers: headersDefault() },
      );
      if (!res.ok) {
        const err = await safeJson(res);
        throw new Error(err?.message || "Failed to fetch latest photo");
      }
      const data = await safeJson(res);
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      const latest = items
        .filter((it) => it && it.picture_front)
        .sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];

      return latest
        ? { date: latest.date, url: latest.picture_front }
        : { date: null, url: null };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to fetch latest photo");
    }
  },
);

export const upsertMeasureDiary = createAsyncThunk(
  "mioPercorso/upsertMeasureDiary",
  async ({ date, patch }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const current = state?.mioPercorso?.entry || {};

      // NOTE: we keep your keys as-is. If backend adds new fields later, add them here.
      const payload = {
        date,
        weight: current?.weight ?? null,
        neck_circumference: current?.neck_circumference ?? null,
        umbilical_circumference: current?.umbilical_circumference ?? null,
        hips_circumference: current?.hips_circumference ?? null,
        middle_thigh_circumference: current?.middle_thigh_circumference ?? null,
        arm_circumference: current?.arm_circumference ?? null,
        // picture_front:
        //   current?.picture_front === undefined ? null : current?.picture_front,
        ...patch,
      };

      const res = await fetch(`${baseUrl}/api/v1/master-club-measure-diary`, {
        method: "POST",
        headers: headersDefault(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await safeJson(res);
        throw new Error(err?.message || "Failed to save measures");
      }

      const data = await safeJson(res);
      return { date, data, patchApplied: patch };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to save measures");
    }
  },
);

export const uploadMeasureDiaryPhoto = createAsyncThunk(
  "mioPercorso/uploadMeasureDiaryPhoto",
  async ({ file }, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("master-club-measure-diary", file);

      const res = await fetch(
        `${baseUrl}/api/v1/file-upload/master-club-measure-diary`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body,
        },
      );

      if (!res.ok) {
        const err = await safeJson(res);
        throw new Error(err?.message || "Failed to upload photo");
      }

      const data = await safeJson(res);
      const filename =
        data?.filename ||
        data?.path ||
        data?.file ||
        data?.data?.filename ||
        null;

      if (!filename)
        throw new Error("Upload succeeded but no filename returned");
      return filename;
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to upload photo");
    }
  },
);

export const uploadAndSaveMeasureDiaryPhoto = createAsyncThunk(
  "mioPercorso/uploadAndSaveMeasureDiaryPhoto",
  async ({ date, file }, { dispatch, rejectWithValue }) => {
    try {
      const filename = await dispatch(
        uploadMeasureDiaryPhoto({ file }),
      ).unwrap();
      await dispatch(
        upsertMeasureDiary({ date, patch: { picture_front: filename } }),
      ).unwrap();
      return { date, picture_front: filename };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to upload and save photo");
    }
  },
);

export const fetchMeasureDiaryHistory = createAsyncThunk(
  "mioPercorso/fetchMeasureDiaryHistory",
  async (
    { date, page = 1, pageSize = 10, direction = "desc", append = false },
    { rejectWithValue },
  ) => {
    try {
      const qs = buildQuery({ date, page, page_size: pageSize, direction });

      let res = await fetch(
        `${baseUrl}/api/v1/master-club-measure-diary/list${qs}`,
        {
          method: "GET",
          headers: headersDefault(),
        },
      );

      if (!res.ok && (res.status === 405 || res.status === 400)) {
        res = await fetch(`${baseUrl}/api/v1/master-club-measure-diary/list`, {
          method: "POST",
          headers: headersDefault(),
          body: JSON.stringify({ date, page, page_size: pageSize, direction }),
        });
      }

      if (!res.ok) {
        const err = await safeJson(res);
        throw new Error(err?.message || "Failed to fetch history");
      }

      const data = await safeJson(res);

      // ✅ Stage API shape:
      // { items: [...], meta: {...} }
      // But keep fallbacks for older variants.
      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

      const meta = data?.meta || data?.data?.meta || null;

      const hasNext =
        meta?.has_next ?? (Array.isArray(items) && items.length >= pageSize);

      const hasPrev = meta?.has_prev ?? false;

      return {
        date,
        page,
        pageSize,
        direction,
        items,
        meta,
        append,
        hasNext,
        hasPrev,
      };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to fetch history");
    }
  },
);

/** ---------------- Preferenze (Diary) ---------------- */

const pad2 = (n) => String(n).padStart(2, "0");
const toLocalYYYYMMDD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const addDaysLocal = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
};

const parseYyyyMmDdLocal = (value) => {
  if (!value) return null;
  const [yearRaw, monthRaw, dayRaw] = String(value).split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const getMondayOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sunday, 1 Monday
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d;
};

export const PROTOCOL_START_NOW = "now";
export const PROTOCOL_START_NEXT_MONDAY = "next_monday";
// From the opening Monday of a percorso announced in the CRM ("Inizia dal"), or
// after the percorso fisso the member is finishing. The server works out the day.
export const PROTOCOL_START_SCHEDULED = "scheduled";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getCalendarPollDateForStartType = (startType) => {
  const today = new Date();
  if (startType === PROTOCOL_START_NEXT_MONDAY) {
    const day = today.getDay();
    const daysUntilMonday = day === 1 ? 7 : (8 - day) % 7;
    return toLocalYYYYMMDD(addDaysLocal(today, daysUntilMonday));
  }
  return toLocalYYYYMMDD(today);
};

/**
 * After timeline store, the calendar API can lag briefly. Poll until
 * diet_plan_range.protocol.id matches, then prime the calendar cache.
 */
const waitForCalendarProtocol = async (dispatch, dateIso, expectedProtocolId) => {
  const maxAttempts = 12;
  const delayMs = 500;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      await sleep(delayMs);
    }
    try {
      const result = await dispatch(getHomeCalendar({ date: dateIso })).unwrap();
      const protocolId = result?.diet_plan_range?.protocol?.id;
      if (Number(protocolId) === Number(expectedProtocolId)) {
        const center = result?.center_date || dateIso;
        clearCalendarCache();
        setCalendarCache(center, result);
        if (dateIso && dateIso !== center) {
          setCalendarCache(dateIso, result);
        }
        return result;
      }
    } catch {
      // retry until maxAttempts
    }
  }

  clearCalendarCache();
  return null;
};

export const computeProtocolEndDate = ({ startDate, duration, scope, explicitEndDate }) => {
  if (explicitEndDate) return explicitEndDate;

  const safeStartDate = parseYyyyMmDdLocal(startDate) || new Date();
  const safeDuration = Number(duration || 0);
  const normalizedScope = String(scope || "").toLowerCase();

  // Calendar (continuativo): align to ISO week, end relative to that Monday
  if (normalizedScope === "calendar") {
    const mondayAnchor = getMondayOfWeek(safeStartDate);
    return toLocalYYYYMMDD(addDaysLocal(mondayAnchor, safeDuration));
  }

  // Fixed (and default): N inclusive days — day 1 = start, day N = start + (N - 1)
  if (safeDuration > 0) {
    return toLocalYYYYMMDD(addDaysLocal(safeStartDate, safeDuration - 1));
  }

  return toLocalYYYYMMDD(safeStartDate);
};

export const fetchPreferenzeGoalsList = createAsyncThunk(
  "mioPercorso/fetchPreferenzeGoalsList",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${baseUrl}/api/v1/nutritional-goal/list`, {
        headers: headersDefault(),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(
          data?.message || "Impossibile caricare gli obiettivi. Riprova.",
        );
      }

      const list = Array.isArray(data) ? data : data?.data ?? [];
      const normalized = list
        .filter((x) => x && x.id != null)
        .map((x) => ({
          id: Number(x.id),
          name: String(x.name ?? ""),
          description: x.description ?? null,
          cardinality: Number(x.cardinality ?? 9999),
          // Kept so the screen can group by category, the same way onboarding
          // does. Dropping them here is what left Preferenze as one flat
          // alphabetical list while onboarding showed the headings.
          categoryName: x.category_name ? String(x.category_name) : null,
          categoryId: x.category_id ?? null,
          categoryOrder: Number.isFinite(Number(x.category_cardinality))
            ? Number(x.category_cardinality)
            : 9999,
          answers: Array.isArray(x.answers) ? x.answers : [],
        }))
        // Category first, then the goal's own place within it — the order the
        // server already sorts by, restated so it survives this normalisation.
        .sort(
          (a, b) =>
            a.categoryOrder - b.categoryOrder ||
            a.cardinality - b.cardinality ||
            a.name.localeCompare(b.name, "it"),
        );

      const selectedIds = normalized
        .filter((g) => Array.isArray(g.answers) && g.answers.length > 0)
        .map((g) => g.id);

      return { goals: normalized, selectedIds };
    } catch (e) {
      return rejectWithValue(e?.message || "Errore rete. Riprova.");
    }
  },
);

export const savePreferenzeSelectedGoals = createAsyncThunk(
  "mioPercorso/savePreferenzeSelectedGoals",
  async (goalIds, { dispatch, rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/master-club-nutritional-goals/`,
        {
          method: "POST",
          headers: headersDefault(),
          body: JSON.stringify({
            nutritional_goals: Array.isArray(goalIds)
              ? goalIds.filter((x) => x != null).map((x) => Number(x))
              : [],
          }),
        },
      );

      const data = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(
          data?.message || "Errore durante il salvataggio. Riprova.",
        );
      }

      // Refresh protocols list (compatibility depends on goals)
      dispatch(fetchPreferenzeCompatibleProtocols());
      return { savedIds: Array.isArray(goalIds) ? goalIds : [] };
    } catch (e) {
      return rejectWithValue(e?.message || "Errore rete. Riprova.");
    }
  },
);

export const fetchPreferenzeCompatibleProtocols = createAsyncThunk(
  "mioPercorso/fetchPreferenzeCompatibleProtocols",
  async (_, { getState, rejectWithValue }) => {
    // This list is refetched right after activating/deactivating a percorso,
    // while the backend is busy generating the new meal plan — the request can
    // come back with a transient 5xx/gateway blip. Retry once with a short
    // backoff before surfacing any failure.
    const requestList = () =>
      fetch(`${baseUrl}/api/v1/master-club-nutritional-goals/list`, {
        headers: headersDefault(),
      });
    try {
      let res = await requestList();
      if (!res.ok && res.status >= 500) {
        await new Promise((resolve) => setTimeout(resolve, 700));
        res = await requestList();
      }
      const data = await safeJson(res);
      if (!res.ok) {
        // `data.message` misses this API's shape — the text sits at
        // error.message — so every failure here read as the generic line below,
        // including a rate limit, which is the one case where the member only
        // needs to wait a moment.
        return rejectWithValue(
          toResponseErrorText(
            res.status,
            data,
            "Impossibile caricare i protocolli. Riprova.",
          ),
        );
      }

      const list = Array.isArray(data) ? data : data?.data ?? [];
      const sorted = [...list].sort(
        (a, b) => Number(b?.compatibility ?? 0) - Number(a?.compatibility ?? 0),
      );

      const activeProtocolId =
        getState()?.user?.master_club_subscriber?.protocols?.current
          ?.protocol_id ?? null;

      return { protocols: sorted, activeProtocolId };
    } catch (e) {
      return rejectWithValue(e?.message || "Errore rete. Riprova.");
    }
  },
);

export const savePreferenzeProtocolTimeline = createAsyncThunk(
  "mioPercorso/savePreferenzeProtocolTimeline",
  async (
    {
      protocol_id,
      start_type: startTypeFromUi,
      fixed_route_id: fixedRouteId,
      // The week of a multi-week percorso fisso the member chose to start on.
      // It used to be missing from this signature, so the value the week
      // dialog collected was handed to this thunk and dropped on the floor:
      // the request never carried it, the server fell back to week 1, and on
      // production not one of 1.314 fixed-route timelines had ever stored
      // anything else.
      start_cycle_week: startCycleWeek,
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const start_type = [
        PROTOCOL_START_NEXT_MONDAY,
        PROTOCOL_START_SCHEDULED,
      ].includes(startTypeFromUi)
        ? startTypeFromUi
        : PROTOCOL_START_NOW;

      const body = {
        protocol_id: Number(protocol_id),
        start_type,
      };
      const fixed_route_id = Number(fixedRouteId);
      if (Number.isFinite(fixed_route_id) && fixed_route_id > 0) {
        body.fixed_route_id = fixed_route_id;
      }

      // Only sent when the member was actually asked. Omitted, the server
      // keeps its own default of week 1, which is right for the percorsi that
      // never present a choice.
      const start_cycle_week = Number(startCycleWeek);
      if (Number.isFinite(start_cycle_week) && start_cycle_week > 0) {
        body.start_cycle_week = start_cycle_week;
      }

      const res = await fetch(
        `${baseUrl}/api/v1/master-club-protocol-timeline/store`,
        {
          method: "POST",
          headers: headersDefault(),
          body: JSON.stringify(body),
        },
      );

      const data = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(
          // The API wraps errors as { error: { message } } — e.g. a percorso
          // with no menu for the chosen week.
          data?.error?.message ||
            data?.message ||
            "Errore durante l'attivazione del protocollo.",
        );
      }

      const protocolId = Number(protocol_id);

      // A booking for a later Monday changes no week the app shows yet, so
      // there is nothing to wait for. Polling next Monday's calendar for it
      // held the popup on its spinner for ~30 s on stage and then gave up
      // (client test, 07/10/2026), so the profile refresh runs in the
      // background and the member gets the confirmation straight away.
      if (start_type === PROTOCOL_START_SCHEDULED) {
        clearCalendarCache();
        dispatch(getUserInfo());
      } else {
        // Refresh profile, then wait until calendar reflects the new protocol
        await dispatch(getUserInfo()).unwrap();
        clearCalendarCache();

        const pollDate = getCalendarPollDateForStartType(start_type);
        await waitForCalendarProtocol(dispatch, pollDate, protocolId);
      }

      return {
        protocol_id: protocolId,
        start_type,
        // The Monday the server booked, for the confirmation message.
        start_date: data?.data?.start_date ?? null,
      };
    } catch (e) {
      return rejectWithValue(e?.message || "Errore rete. Riprova.");
    }
  },
);

export const savePreferenzeDiet = createAsyncThunk(
  "mioPercorso/savePreferenzeDiet",
  async (
    { keep_weight, vegetarian, gluten_free, is_smart },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const payload = {
        keep_weight: !!keep_weight,
        vegetarian: typeof vegetarian === "boolean" ? vegetarian : false,
        gluten_free: !!gluten_free,
        is_smart: !!is_smart,
      };

      await patchJson(`${baseUrl}/api/master-club-subscribers/1`, payload);

      Object.entries(payload).forEach(([key, value]) => {
        dispatch(
          updateUserProperty({
            stateKey: "master_club_subscriber",
            key,
            value,
          }),
        );
      });

      try {
        await dispatch(getUserInfo()).unwrap();
      } catch {
        // Preference save succeeded; do not fail the confirmation because
        // the follow-up profile refresh hit a transient network/rate issue.
      }
      return payload;
    } catch (e) {
      return rejectWithValue(friendlyReadError(e, "Errore rete. Riprova."));
    }
  },
);
